import React, { useState, useEffect, useRef } from 'react';
import {
  Header,
  Table,
  Box,
  SpaceBetween,
  Pagination,
  TextFilter,
  CollectionPreferences,
  FormField,
  Input,
  Button,
  StatusIndicator
} from '@amzn/awsui-components-react/polaris';
import { StageDetail } from './StageDetail';
import PropTypes from 'prop-types';
import { getColumns } from './columns';
import ElementErrorBoundary from '../../../components/ErrorBoundary/ElementErrorBoundary';
import { Grid } from '@amzn/awsui-components-react';
import { useSdpdsReadOnlyOperation } from '../../../hooks/useSdpdsReadOnlyOperation';

export const ComponentReleaseTrackerDetail = ({ stackName }) => {
  const [releases, setReleases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReleaseId, setSelectedReleaseId] = useState(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteringText, setFilteringText] = useState('');
  const [releaseIdInput, setReleaseIdInput] = useState('');
  const [isFetchingStatus, setIsFetchingStatus] = useState(false);
  const stageDetailRef = useRef(null);

  const { refetch: refetchActiveReleases } = useSdpdsReadOnlyOperation({
    operation_name: 'GetActiveReleasesForStack',
    payload: { stackName }
  });

  const { refetch: refetchReleaseStatus } = useSdpdsReadOnlyOperation({
    operation_name: 'GetReleaseStatus',
    payload: { stackName, releaseId: null }
  });

  const fetchActiveReleases = async () => {
    if (!stackName) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await refetchActiveReleases({
        payload: { stackName }
      });

      if (!result?.data?.sdpdsReadOnlyOperation) {
        setError('Failed to fetch active releases');
        setIsLoading(false);
        return;
      }

      const response = result.data.sdpdsReadOnlyOperation;

      if (response.error) {
        setError(response.error);
      } else {
        setReleases(response.releases || []);
        setError(null);
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching releases');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReleaseStatus = async () => {
    if (!releaseIdInput || !stackName) return;

    setIsFetchingStatus(true);
    try {
      const result = await refetchReleaseStatus({
        payload: { stackName, releaseId: releaseIdInput }
      });

      if (!result?.data?.sdpdsReadOnlyOperation) {
        setError('Failed to fetch release status');
        setIsFetchingStatus(false);
        return;
      }

      const response = result.data.sdpdsReadOnlyOperation;

      if (response.error) {
        setError(response.error);
      } else {
        const transformedRelease = {
          releaseId: response.releaseId,
          status: response.status,
          auditProperties: {
            createdBy: response.auditProperties?.createdBy || 'N/A',
          },
          creationTime: response.auditProperties?.creationTime || 'N/A',
          lastModificationTime: response.auditProperties?.lastModificationTime || 'N/A',
          lastModifiedBy: response.auditProperties?.lastModifiedBy || 'N/A'
        };

        setReleases(prevReleases => {
          const filteredReleases = prevReleases.filter(
            release => release.releaseId !== releaseIdInput
          );
          return [transformedRelease, ...filteredReleases];
        });
        setReleaseIdInput('');
        setError(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch release status');
    } finally {
      setIsFetchingStatus(false);
    }
  };

  const handleKeyDown = ({ detail }) => {
    if (detail.key === 'Enter' && releaseIdInput && !isFetchingStatus) {
      fetchReleaseStatus();
    }
  };

  const handleReleaseSelect = (releaseId) => {
    setSelectedReleaseId(releaseId);
    setTimeout(scrollToStageDetail, 100);
  };

  useEffect(() => {
    fetchActiveReleases();
  }, [stackName]);

  const getFilteredItems = () => {
    if (!filteringText) return releases;
    const searchText = filteringText.toLowerCase();
    return releases.filter(item => {
      if (item.releaseId?.toLowerCase().includes(searchText)) return true;
      if (item.status?.toLowerCase().includes(searchText)) return true;
      const createdBy = item.auditProperties?.createdBy;
      if (typeof createdBy === 'string' && createdBy.toLowerCase().includes(searchText)) return true;
      if (createdBy?.id?.toLowerCase().includes(searchText)) return true;
      if (item.auditProperties?.creationTime?.toString().includes(searchText)) return true;
      if (item.auditProperties?.lastModificationTime?.toString().includes(searchText)) return true;
      return false;
    });
  };

  const getPagedItems = () => {
    const filteredItems = getFilteredItems();
    const startIndex = (currentPageIndex - 1) * pageSize;
    return filteredItems.slice(startIndex, startIndex + pageSize);
  };

  const filteredItems = getFilteredItems();
  const items = getPagedItems();
  const columnDefinitions = getColumns(handleReleaseSelect);

  const scrollToStageDetail = () => {
    if (stageDetailRef.current) {
      const yOffset = -20;
      const element = stageDetailRef.current;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  return (
    <ElementErrorBoundary>
      {isLoading ? (
        <Box textAlign="center" padding={{ vertical: "l" }}>
          <SpaceBetween size="xs">
            <div>Loading...</div>
          </SpaceBetween>
        </Box>
      ) : (
        <SpaceBetween size="l">
          <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
            <FormField label="Release ID">
              <Input
                value={releaseIdInput}
                onChange={({ detail }) => setReleaseIdInput(detail.value)}
                onKeyDown={handleKeyDown}
                placeholder="Enter release ID"
              />
            </FormField>
            <Box padding={{ top: "l" }}>
              <Button
                onClick={fetchReleaseStatus}
                loading={isFetchingStatus}
                disabled={!releaseIdInput}
              >
                Fetch Release Status
              </Button>
            </Box>
          </Grid>

          {error && (
            <Box color="text-status-error" padding={{ vertical: "s" }}>
              <SpaceBetween size="xs">
                <StatusIndicator type="error">
                  {error}
                </StatusIndicator>
              </SpaceBetween>
            </Box>
          )}

          <Table
            columnDefinitions={columnDefinitions}
            items={items}
            loading={isLoading}
            loadingText="Loading releases"
            filter={
              <TextFilter
                filteringPlaceholder="Filter data across all fields"
                filteringText={filteringText}
                onChange={({ detail }) => {
                  setFilteringText(detail.filteringText);
                  setCurrentPageIndex(1);
                }}
                countText={`${filteredItems.length} matches`}
              />
            }
            pagination={
              <Pagination
                currentPageIndex={currentPageIndex}
                onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
                pagesCount={Math.ceil(filteredItems.length / pageSize)}
                pageSize={pageSize}
              />
            }
            preferences={
              <CollectionPreferences
                title="Preferences"
                confirmLabel="Confirm"
                cancelLabel="Cancel"
                preferences={{ pageSize }}
                pageSizePreference={{
                  title: "Page size",
                  options: [
                    { value: 10, label: "10 releases" },
                    { value: 20, label: "20 releases" },
                    { value: 50, label: "50 releases" }
                  ]
                }}
                onConfirm={({ detail }) => {
                  setPageSize(detail.pageSize);
                  setCurrentPageIndex(1);
                }}
              />
            }
            empty={
              <Box textAlign="center" color="inherit">
                <b>No active releases found for this stack</b>
              </Box>
            }
            header={
              <Header counter={`(${filteredItems.length})`}>
                Active Releases
              </Header>
            }
            variant="embedded"
            stickyHeader={false}
          />
          {selectedReleaseId && (
            <div ref={stageDetailRef}>
              <StageDetail
                releaseId={selectedReleaseId}
                stackName={stackName}
              />
            </div>
          )}
        </SpaceBetween>
      )}
    </ElementErrorBoundary>
  );
};

ComponentReleaseTrackerDetail.propTypes = {
  stackName: PropTypes.string.isRequired
};
